import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Mui from "@mui/material";
import { ViewWeek, DragIndicator, LockOutlined } from "@mui/icons-material";
import { withFixedLeadingKeys } from "./fixedLeadingColumns";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import { themes } from "services/constants";

// How close to the top/bottom edge of the scrollable row list (in px)
// triggers auto-scroll while dragging, and how fast it scrolls at most.
const AUTO_SCROLL_EDGE_SIZE = 48;
const AUTO_SCROLL_MAX_SPEED = 14;

const iconButtonStyle = {
  backgroundColor: "action.hover",
  borderRadius: "50%",
  border: "1px solid",
  borderColor: "divider",
  color: "text.primary",
  opacity: 1,
  transition: "none",
  "&.Mui-disabled": {
    backgroundColor: "action.hover",
    color: "text.primary",
    opacity: 1,
  },
  "&:hover": { backgroundColor: "action.hover" },
};

// Shared "Save View" popover: lets a user pick a subset of a table's columns,
// drag to reorder them, and rename a column's label for that saved view only
// (the underlying data key never changes). Any module can drop this in via
// CustomFilter's `newButtonComponent` slot alongside its own persistence
// logic (see SalaryStatementReport for the reference wiring) without
// changing the shared CustomFilter.js itself.
export const SaveViewButton = forwardRef(function SaveViewButton(
  {
    headers = [],
    screenAccess,
    activeView = null,
    onCreateSavedView,
    onUpdateSavedView,
    hideTrigger = false,
    createTitle = "Save Header View",
    editTitle = "Edit Header View",
    createTooltip = "Save View",
    editTooltip = "Edit View",
    namePlaceholder = "Enter view name",
    createInitialHeaderConfig = null,
    checkboxSx = null,
    cancelButtonSx = null,
    submitButtonSx = null,
    // Column keys that always come first, in this order, and can't be
    // dragged (they can still be ticked/unticked and renamed).
    fixedLeadingKeys = null,
  },
  ref,
) {
  // Puts the fixed columns on top and marks them so they can't be dragged.
  const applyFixedRows = (list) => {
    if (!fixedLeadingKeys?.length) return list;
    const fixedSet = new Set(fixedLeadingKeys);
    return withFixedLeadingKeys(list, fixedLeadingKeys).map((row) => ({
      ...row,
      fixed: fixedSet.has(row.key),
    }));
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewName, setViewName] = useState("");
  const [viewNameError, setViewNameError] = useState("");
  const [rows, setRows] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [editingViewId, setEditingViewId] = useState(null);

  const open = Boolean(anchorEl);
  const rowsContainerRef = useRef(null);
  const autoScrollFrameRef = useRef(null);
  const autoScrollSpeedRef = useRef(0);

  const stopAutoScroll = () => {
    if (autoScrollFrameRef.current) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    autoScrollSpeedRef.current = 0;
  };

  const runAutoScrollStep = () => {
    const container = rowsContainerRef.current;
    if (!container || autoScrollSpeedRef.current === 0) {
      autoScrollFrameRef.current = null;
      return;
    }

    container.scrollTop += autoScrollSpeedRef.current;
    autoScrollFrameRef.current = requestAnimationFrame(runAutoScrollStep);
  };

  const setAutoScrollSpeed = (speed) => {
    autoScrollSpeedRef.current = speed;
    if (speed !== 0 && !autoScrollFrameRef.current) {
      autoScrollFrameRef.current = requestAnimationFrame(runAutoScrollStep);
    }
  };

  useEffect(() => stopAutoScroll, []);

  const openPopover = (
    anchorElement,
    { forceCreate = false, explicitView = null } = {},
  ) => {
    if (!screenAccess) {
      showToast("error", "Access denied: You are not authorized to save views");
      return;
    }

    const viewToEdit = explicitView || activeView;

    if (!forceCreate && viewToEdit) {
      // Edit mode: reopen pre-filled with the active view's own name, its
      // saved column order/labels (checked), plus any other columns the
      // view doesn't include yet (unchecked), so the user can adjust it
      // instead of always starting from a blank view.
      const configuredEntries = viewToEdit.headerConfig?.length
        ? viewToEdit.headerConfig
        : (viewToEdit.headerKeys || []).map((key) => ({ key }));
      const configuredKeys = new Set(
        configuredEntries.map((entry) => entry.key),
      );

      const configuredRows = configuredEntries
        .map((entry) => {
          const header = headers.find((h) => h.key === entry.key);
          if (!header) return null;
          return {
            key: entry.key,
            name: entry.name || header.name,
            originalName: header.name,
            groupName: header.groupName || null,
            lockGroup: header.lockGroup || null,
            selected: true,
          };
        })
        .filter(Boolean);

      const remainingRows = headers
        .filter((header) => !configuredKeys.has(header.key))
        .map((header) => ({
          key: header.key,
          name: header.name,
          originalName: header.name,
          groupName: header.groupName || null,
          lockGroup: header.lockGroup || null,
          selected: false,
        }));

      setRows(applyFixedRows([...configuredRows, ...remainingRows]));
      setViewName(viewToEdit.name || "");
      setViewNameError("");
      setEditingViewId(viewToEdit.id);
    } else {
      if (createInitialHeaderConfig?.length) {
        const configuredKeys = new Set(
          createInitialHeaderConfig.map((entry) => entry.key),
        );
        const configuredRows = createInitialHeaderConfig
          .map((entry) => {
            const header = headers.find((item) => item.key === entry.key);
            if (!header) return null;
            return {
              key: entry.key,
              name: entry.name || header.name,
              originalName: header.name,
              groupName: header.groupName || null,
              lockGroup: header.lockGroup || null,
              selected: true,
            };
          })
          .filter(Boolean);

        const remainingRows = headers
          .filter((header) => !configuredKeys.has(header.key))
          .map((header) => ({
            key: header.key,
            name: header.name,
            originalName: header.name,
            groupName: header.groupName || null,
            lockGroup: header.lockGroup || null,
            selected: false,
          }));

        setRows(applyFixedRows([...configuredRows, ...remainingRows]));
      } else {
        setRows(
          applyFixedRows(
            headers.map((header) => ({
              key: header.key,
              name: header.name,
              originalName: header.name,
              groupName: header.groupName || null,
              lockGroup: header.lockGroup || null,
              selected: true,
            })),
          ),
        );
      }
      setViewName("");
      setViewNameError("");
      setEditingViewId(null);
    }

    setAnchorEl(anchorElement);
  };

  const handleOpen = (event) => openPopover(event.currentTarget);

  useImperativeHandle(ref, () => ({
    openCreate: (anchorElement) =>
      openPopover(anchorElement, { forceCreate: true }),
    openEdit: (anchorElement, view) =>
      openPopover(anchorElement, { explicitView: view }),
  }));

  const handleClose = () => {
    stopAutoScroll();
    setAnchorEl(null);
    setViewName("");
    setViewNameError("");
    setRows([]);
    setDragIndex(null);
    setEditingViewId(null);
  };

  const toggleRowSelected = (key) => {
    setRows((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, selected: !row.selected } : row,
      ),
    );
  };

  const renameRow = (key, name) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, name } : row)),
    );
  };

  const getLockBounds = (list, index) => {
    const lockGroup = list[index]?.lockGroup || null;
    if (!lockGroup) {
      return { start: index, end: index, lockGroup: null };
    }

    let start = index;
    let end = index;

    while (start > 0 && list[start - 1]?.lockGroup === lockGroup) {
      start -= 1;
    }

    while (end < list.length - 1 && list[end + 1]?.lockGroup === lockGroup) {
      end += 1;
    }

    return { start, end, lockGroup };
  };

  // Partitions the flat `rows` array into contiguous runs sharing the same
  // groupName, keeping each row's ORIGINAL index (drag handlers below all
  // address rows by their position in the full flat array, not a per-segment
  // one). Rows never actually interleave across groups — handleDragOver
  // below refuses to reorder across a group boundary — so this always
  // produces one segment per group, in a stable order. A screen with no
  // groupName at all (the common case) yields exactly one ungrouped segment,
  // which renders with no box/title below — zero visual change from before.
  const rowSegments = useMemo(() => {
    const segments = [];
    rows.forEach((row, index) => {
      const groupName = row.groupName || null;
      const last = segments[segments.length - 1];
      if (last && last.groupName === groupName) {
        last.items.push({ row, index });
      } else {
        segments.push({ groupName, items: [{ row, index }] });
      }
    });
    return segments;
  }, [rows]);

  const handleDragStart = (index) => {
    if (rows[index]?.fixed) return;
    const { start } = getLockBounds(rows, index);
    setDragIndex(start);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    // Nothing can be dropped into the fixed columns at the top.
    if (rows[index]?.fixed || rows[dragIndex]?.fixed) return;

    // Grouped columns (e.g. "Old Salary"'s children) may only reorder
    // among their own group, and ungrouped columns only among each other —
    // otherwise dragging an ungrouped column in between a group's children
    // would split that group into two duplicated header segments.
    const draggedGroup = rows[dragIndex]?.groupName || null;
    const targetGroup = rows[index]?.groupName || null;
    if (draggedGroup !== targetGroup) return;

    const draggedBounds = getLockBounds(rows, dragIndex);
    if (index >= draggedBounds.start && index <= draggedBounds.end) return;

    const targetBounds = getLockBounds(rows, index);
    const normalizedTargetIndex =
      targetBounds.lockGroup &&
      targetBounds.lockGroup !== draggedBounds.lockGroup
        ? targetBounds.start
        : index;

    setRows((prev) => {
      const next = [...prev];
      const currentDraggedBounds = getLockBounds(prev, dragIndex);
      const movedRows = next.splice(
        currentDraggedBounds.start,
        currentDraggedBounds.end - currentDraggedBounds.start + 1,
      );
      const insertionIndex =
        currentDraggedBounds.start < normalizedTargetIndex
          ? normalizedTargetIndex - movedRows.length
          : normalizedTargetIndex;

      next.splice(insertionIndex, 0, ...movedRows);
      return next;
    });
    setDragIndex(
      draggedBounds.start < normalizedTargetIndex
        ? normalizedTargetIndex - (draggedBounds.end - draggedBounds.start + 1)
        : normalizedTargetIndex,
    );
  };

  const handleDragEnd = () => {
    stopAutoScroll();
    setDragIndex(null);
  };

  // Native HTML5 drag-and-drop doesn't auto-scroll a scrollable container
  // while dragging near its edges, so with a long header list (e.g. 30+
  // columns) the target row can be scrolled out of view and unreachable.
  // This mirrors it near the top/bottom edge of the row list.
  const handleRowsContainerDragOver = (event) => {
    event.preventDefault();
    const container = rowsContainerRef.current;
    if (!container) return;

    const { top, bottom } = container.getBoundingClientRect();
    const pointerY = event.clientY;
    const distanceFromTop = pointerY - top;
    const distanceFromBottom = bottom - pointerY;

    if (distanceFromTop < AUTO_SCROLL_EDGE_SIZE) {
      const proximity = Math.max(0, AUTO_SCROLL_EDGE_SIZE - distanceFromTop);
      setAutoScrollSpeed(
        -Math.ceil((proximity / AUTO_SCROLL_EDGE_SIZE) * AUTO_SCROLL_MAX_SPEED),
      );
    } else if (distanceFromBottom < AUTO_SCROLL_EDGE_SIZE) {
      const proximity = Math.max(0, AUTO_SCROLL_EDGE_SIZE - distanceFromBottom);
      setAutoScrollSpeed(
        Math.ceil((proximity / AUTO_SCROLL_EDGE_SIZE) * AUTO_SCROLL_MAX_SPEED),
      );
    } else {
      setAutoScrollSpeed(0);
    }
  };

  const handleSave = () => {
    const trimmedViewName = viewName.trim();

    if (!trimmedViewName) {
      setViewNameError("Please enter a view name.");
      return;
    }
    setViewNameError("");

    const selectedRows = rows.filter((row) => row.selected);

    if (selectedRows.length === 0) {
      showToast("error", "Select at least one header.");
      return;
    }

    const headerConfig = selectedRows.map((row) => ({
      key: row.key,
      name: row.name.trim() || row.originalName,
    }));

    if (editingViewId) {
      onUpdateSavedView?.(editingViewId, trimmedViewName, headerConfig);
    } else {
      onCreateSavedView?.(trimmedViewName, headerConfig);
    }
    handleClose();
  };

  return (
    <>
      {!hideTrigger && (
        <Mui.Tooltip
          slotProps={tooltipStyleProps}
          title={activeView ? editTooltip : createTooltip}
          arrow
        >
          <span>
            <Mui.IconButton
              onClick={handleOpen}
              disabled={headers.length === 0}
              sx={iconButtonStyle}
            >
              <ViewWeek />
            </Mui.IconButton>
          </span>
        </Mui.Tooltip>
      )}

      <Mui.Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 1, width: 340, borderRadius: 2, p: 1.5 } }}
      >
        <Mui.Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Mui.Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {editingViewId ? editTitle : createTitle}
          </Mui.Typography>

          <Mui.TextField
            size="small"
            placeholder={namePlaceholder}
            value={viewName}
            onChange={(event) => {
              setViewName(event.target.value);
              if (viewNameError) {
                setViewNameError("");
              }
            }}
            error={Boolean(viewNameError)}
            helperText={viewNameError || undefined}
            fullWidth
          />
          <Mui.Box
            ref={rowsContainerRef}
            onDragOver={handleRowsContainerDragOver}
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
            }}
          >
            {rowSegments.map((segment, segmentIndex) => (
              <Mui.Box
                key={segment.groupName || `ungrouped-${segmentIndex}`}
                sx={
                  segment.groupName
                    ? {
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 0.75,
                        mb: 1,
                      }
                    : { mb: segmentIndex < rowSegments.length - 1 ? 1 : 0 }
                }
              >
                {segment.groupName && (
                  <Mui.Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      mb: 0.5,
                      px: 0.5,
                    }}
                  >
                    {segment.groupName}
                  </Mui.Typography>
                )}
                {segment.items.map(({ row, index }) => (
                  <Mui.Box
                    key={row.key}
                    draggable={!row.fixed}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(event) => handleDragOver(event, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      py: 0.25,
                      px: 0.5,
                      mb: 0.25,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        dragIndex === index ? "warning.main" : "transparent",
                      backgroundColor:
                        dragIndex === index
                          ? "action.selected"
                          : "transparent",
                      cursor: row.fixed ? "default" : "grab",
                    }}
                  >
                    {row.fixed ? (
                      <Mui.Tooltip
                        title="Fixed position"
                        arrow
                        slotProps={tooltipStyleProps}
                      >
                        <LockOutlined
                          fontSize="small"
                          sx={{ color: "text.disabled", flexShrink: 0 }}
                        />
                      </Mui.Tooltip>
                    ) : (
                      <DragIndicator
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                      />
                    )}
                    <Mui.Checkbox
                      checked={row.selected}
                      onChange={() => toggleRowSelected(row.key)}
                      size="small"
                      sx={
                        checkboxSx || undefined
                      }
                    />
                    <Mui.Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Mui.TextField
                        size="small"
                        value={row.name}
                        onChange={(event) =>
                          renameRow(row.key, event.target.value)
                        }
                        variant="standard"
                        fullWidth
                        sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
                      />
                    </Mui.Box>
                  </Mui.Box>
                ))}
              </Mui.Box>
            ))}
          </Mui.Box>

          <Mui.Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Mui.Button
              size="small"
              onClick={handleClose}
              sx={{
                ...(cancelButtonSx || {}),
              }}
            >
              Cancel
            </Mui.Button>
            <Mui.Button
              size="small"
              onClick={handleSave}
              sx={{
                ...(submitButtonSx || {
                  background: themes.primaryButton,
                  color: themes.headLine,
                  fontWeight: "bold",
                  textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: themes.primaryButton,
                  },
                }),
              }}
            >
              {editingViewId ? "Update" : "Save"}
            </Mui.Button>
          </Mui.Box>
        </Mui.Box>
      </Mui.Popover>
    </>
  );
});
